import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  LayoutDashboard, ChartGantt, Clock, Users, ChartColumn, PanelsTopLeft,
  CalendarDays, Calendar, ClipboardList, Database, FlaskConical, GraduationCap, CircleCheck,
  SlidersHorizontal, Ticket, NotebookTabs, Wallet, Milestone, Link2,
  Wrench, PanelLeftClose, PanelLeftOpen, CircleQuestionMark, CircleDollarSign,
} from 'lucide-react';

// Pages
import Dashboard from './pages/Dashboard';
import PortfolioTimeline from './pages/PortfolioTimeline';
import ProjectLaunch from './pages/ProjectLaunch';
import BPATracker from './pages/BPATracker';
import ConfigTracker from './pages/ConfigTracker';
import TicketStatus from './pages/TicketStatus';
import TimeEntry from './pages/TimeEntry';
import ResourcePlanning from './pages/ResourcePlanning';
import TimeReports from './pages/TimeReports';
import RndTimeEntry from './pages/RndTimeEntry';
import Administration from './pages/Administration';
import DataConversion from './pages/DataConversion';
import SolutionTesting from './pages/SolutionTesting';
import TrainingTracker from './pages/TrainingTracker';
import GoLiveReadiness from './pages/GoLiveReadiness';
import Maintenance from './pages/Maintenance';
import LoginHistory from './pages/LoginHistory';
import BillingRates from './pages/BillingRates';
import SuggestionReviews from './pages/SuggestionReviews';
import ProjectSchedule from './pages/ProjectSchedule';
import ProjectCalendar from './pages/ProjectCalendar';
import ProjectRegisters from './pages/ProjectRegisters';
import ProjectLinks from './pages/ProjectLinks';
import ProjectBudgets from './pages/ProjectBudgets';
import MilestoneTracker from './pages/MilestoneTracker';
import RevenueTracker from './pages/RevenueTracker';
import ViewAsMenu from './ViewAsMenu';
import { clearPreviewScope } from './customerPreview';

// Shared state / gates
import { useApp } from './appContext';
import { useNavGuardConfirm } from './unsavedChangesGuard';
import { MaintenanceBanner } from './MaintenanceGate';
import HelpDrawer from './help/HelpDrawer';
import PeterWidget from './help/PeterWidget';
import SuggestionBox from './suggestions/SuggestionBox';
import WhatsNewPopup from './suggestions/WhatsNewPopup';
import NotificationBell from './notifications/NotificationBell';
import IdleLogout from './IdleLogout';
import { demoSignOut, getDemoUser, isDemoMode } from './demoSession';
import ThemeToggle from './ThemeToggle';
import PulseMark from './PulseMark';
import { COPYRIGHT_NOTICE } from './legalNotice';

// Styles
import './App.css';

// Project Schedule reads exclusively from our own API — unlike SOW Intake and Project Setup there is
// no SharePoint/OnePlan path for it to fall back to, so a REACT_APP_*_SOURCE toggle makes no sense.
// It's gated at the nav/route level instead: apps/api has no deploy target yet, so in any deployed
// environment the page could only render an error. Off unless explicitly switched on.
const SCHEDULING_ENABLED = process.env.REACT_APP_SCHEDULING_ENABLED === 'true';

// Admin lives behind one header link (top-right, left of the user name), not in this list. That link
// lands on Administration (`/administration`, `Administration.js`) -- one tabbed hub for Configuration,
// Project Configuration, Customer Access and AI Settings (folded together 2026-09-30; each used to be
// its own page linking to the others). Maintenance Mode and Login History stay separate pages, linked
// from Administration's own corner rather than folded in as tabs (Jeremy, 2026-09-30).
//
// Administration deliberately isn't in the "Project Workstreams" section: that slot is reserved for
// the real per-project "Configuration" workstream (Aumentum software config,
// packages/shared/src/scheduling.ts) once that page gets built -- a different thing entirely from this
// admin/org settings page, and the two were never meant to share a nav slot.
//
// `{ type: 'section' }` entries render as a non-clickable group label rather than a NavLink — used to
// group the schedule-building pages under "Project Management" without needing a nested array.
//
// `icon` is a lucide-react component, not a string — sized/colored centrally by NAV_ICON_PROPS and
// `.nav-icon` so every glyph shares one stroke weight and optical size (emojis couldn't, they render
// per-platform). Pick from lucide's current names; the legacy aliases (BarChart3, CheckCircle2, …)
// log deprecation warnings in dev.
// Every signed-in user sees every item (Jeremy, 2026-08-13, reverting the 2026-08-12 role tiers).
// The API's per-request role checks are the real boundary, so the nav offering a page a member
// can't act in is a cosmetic problem, not a security one.
const NAV_ITEMS = [
  { path: '/',          label: 'Dashboard',          icon: LayoutDashboard },
  { path: '/timeline',  label: 'Portfolio Timeline', icon: ChartGantt },
  // Portfolio-wide, same as Portfolio Timeline just above -- moved up from "Project Management"
  // (2026-09-10), which is otherwise per-project pages.
  { path: '/milestone-tracker', label: 'Milestone Tracker', icon: Milestone },
  { path: '/revenue-tracker', label: 'Revenue Tracker', icon: CircleDollarSign },
  { type: 'section',    label: 'Resourcing' },
  { path: '/my-time',   label: 'My Time',            icon: Clock },
  { path: '/resource-planning', label: 'Resource Planning', icon: Users },
  { path: '/time-reports', label: 'Time Reports',    icon: ChartColumn },
  { type: 'section',    label: 'Project Management' },
  // Replaces the old separate "SOW Intake"/"Project Setup" nav entries (2026-09-07) -- both pages
  // are unchanged underneath and still routed below, just reached from this hub's checklist items
  // now instead of being nav destinations themselves.
  { path: '/project-launch', label: 'Project Details', icon: PanelsTopLeft },
  ...(SCHEDULING_ENABLED
    ? [{ path: '/schedule', label: 'Project Schedule', icon: CalendarDays }]
    : []),
  // One nav item, four tabbed registers inside it (Jeremy, 2026-09-03) -- the RAAIDD register that
  // used to sit here at /risk-register is now one of its tabs.
  { path: '/registers', label: 'Project Registers', icon: NotebookTabs },
  // Pulled forward ahead of Financials' December roadmap slot (Jeremy, 2026-09-09), same precedent
  // as bpa/risk-register -- see the Project Details tab's Budget frame for the summary view this
  // full page manages.
  { path: '/project-budgets', label: 'Project Budgets', icon: Wallet },
  // Reads across scheduling/bpa/risk-register/project-registers -- no module of its own owns a
  // table for this, see the `calendar` module. Not gated like Schedule/BPA were: apps/api has been a
  // live deploy target since Phase D/E, so there's no "built but nothing to call" period to hide.
  { path: '/calendar', label: 'Project Calendar', icon: Calendar },
  // Standalone nav item rather than a Project Registers tab (Jeremy, 2026-09-17) -- it's a link to
  // somewhere else, not a log kept alongside the others. Mirrors the customer portal's own /links.
  { path: '/links', label: 'Project Links', icon: Link2 },
  { type: 'section',    label: 'Project Workstreams' },
  { path: '/bpa',       label: 'Business Process Analysis', icon: ClipboardList },
  { path: '/config',    label: 'Config Tracker',     icon: SlidersHorizontal },
  { path: '/conversion',label: 'Data Conversion',    icon: Database },
  { path: '/testing',   label: 'Solution Testing',   icon: FlaskConical },
  { path: '/training',  label: 'Training',           icon: GraduationCap },
  { type: 'section',    label: 'Project Go-Live' },
  { path: '/golive',    label: 'Go-Live Readiness',  icon: CircleCheck },
  // Support tickets are a post-go-live concern, so they sit here rather than under Implementation.
  { path: '/tickets',   label: 'TS Ticket Dashboards',   icon: Ticket },
];

// One place to change size/weight for every nav glyph. 1.75 rather than lucide's default 2: at 18px
// on the dark sidebar the denser icons (Database, ChartGantt) start filling in at 2.
const NAV_ICON_PROPS = { size: 18, strokeWidth: 1.75, 'aria-hidden': true };

function App() {
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Peter's own open/collapsed state. Deliberately NOT tied to whether his component is mounted —
  // PeterWidget (and the PeterChat inside it) stays mounted for the app's whole lifetime, so a
  // conversation survives both collapsing the panel and navigating to another page. This is only
  // ever about whether the panel is currently the thing on screen.
  const [peterOpen, setPeterOpen] = useState(false);
  // A citation clicked inside Peter's answer names a help topic; this is the handoff to HelpDrawer,
  // consumed once so a later plain "Help" open doesn't reuse a stale target (see HelpDrawer).
  const [pendingHelpTopicId, setPendingHelpTopicId] = useState(null);
  // Whether the Suggest popup is open -- the header button's own state, lifted here only because
  // SuggestionBox needs it as a controlled prop. A suggestion notification navigates straight to
  // /suggestions now (NotificationBell.js) rather than opening this popup.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const { instance } = useMsal();
  const { currentUser } = useApp();

  // Help and Peter share the bottom-right corner of the screen, so only one is ever the thing you're
  // looking at there — opening one collapses the other rather than stacking two floating panels.
  const openHelp = () => { setHelpOpen(true); setPeterOpen(false); };
  const setPeterOpenExclusive = (next) => { setPeterOpen(next); if (next) setHelpOpen(false); };
  const openHelpAtTopic = (topicId) => {
    setPendingHelpTopicId(topicId);
    setHelpOpen(true);
    setPeterOpen(false);
  };

  // "?" opens help, the convention everywhere else it exists — but not while someone is typing a
  // question mark into a comment or a search box.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      openHelp();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Signed-in identity comes from the Entra token, not a hardcoded name.
  const account = instance.getActiveAccount();
  const displayName = (isDemoMode ? getDemoUser()?.name : null) || account?.name || account?.username || 'Signed in';

  const isAdmin = currentUser?.role === 'admin';
  // NavGuardProvider wraps <App/> itself in AppShell.js (not the customer shell, which has no
  // write affordances to guard) -- every exit from the app that isn't a route change (Maintenance,
  // Sign out) is guarded here too, alongside the side nav and, in ProjectRegisters.js, the tab bar
  // and project picker.
  const confirmNavigation = useNavGuardConfirm();

  return (
    <Router>
      <div className="app-shell">

        {/* Signs an unattended browser out rather than leaving a live session open overnight. */}
        <IdleLogout />

        {/* Only renders when maintenance mode is on — admins see this while everyone else is
            locked out, so it's obvious the site is closed. */}
        <MaintenanceBanner />

        {/* Internal shell only (the customer portal has its own shell) — Friday "What's new". */}
        <WhatsNewPopup />

        {/* TOP HEADER */}
        <header className="app-header">
          <div className="header-left">
            <button
              className="nav-toggle"
              onClick={() => setNavCollapsed(!navCollapsed)}
              aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {navCollapsed
                ? <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden />
                : <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden />}
            </button>
            <div className="header-brand">
              <div className="header-brand-lockup">
                <PulseMark size={38} trim />
                <span className="header-title">Pulse</span>
              </div>
            </div>
          </div>
          <div className="header-right">
            <ThemeToggle />
            {isAdmin && (
              <div className="header-admin-links">
                <NavLink
                  to="/administration"
                  className={({ isActive }) => `header-admin-link ${isActive ? 'active' : ''}`}
                  onClick={(e) => { if (!confirmNavigation()) e.preventDefault(); }}
                >
                  <Wrench size={15} strokeWidth={1.75} aria-hidden />
                  Admin
                </NavLink>
              </div>
            )}
            <NotificationBell />
            <SuggestionBox
              open={suggestionsOpen}
              onOpen={() => setSuggestionsOpen(true)}
              onClose={() => setSuggestionsOpen(false)}
            />
            {/* Help sits with the identity cluster rather than the nav: it's about the screen you're
                on, and every screen has it in the same place. */}
            <button
              className="header-help-btn"
              onClick={openHelp}
              title="Help for this screen (?)"
              aria-label="Help"
            >
              <CircleQuestionMark size={17} strokeWidth={1.75} aria-hidden />
              Help
            </button>
            <ViewAsMenu displayName={displayName} />
            <button
              className="btn btn-outline"
              style={{ fontSize: 12, padding: '4px 12px', marginLeft: 12 }}
              onClick={() => {
                if (!confirmNavigation()) return;
                clearPreviewScope();
                if (isDemoMode) demoSignOut();
                else instance.logoutRedirect();
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        <div className="app-body">

          {/* SIDE NAV */}
          <nav className={`side-nav ${navCollapsed ? 'collapsed' : ''}`}>
            {NAV_ITEMS.map(item => {
              if (item.type === 'section') {
                // No icon to fall back on when collapsed, so the label would just be blank space.
                return navCollapsed ? null : (
                  <div key={item.label} className="nav-section-label">{item.label}</div>
                );
              }
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  // Collapsed, the label is gone and the icon is the only affordance left.
                  title={navCollapsed ? item.label : undefined}
                  // A register mid-edit (see unsavedChangesGuard.js) gets one confirm before leaving,
                  // same as a browser refresh/close would -- cancelling stays on the current page.
                  onClick={(e) => { if (!confirmNavigation()) e.preventDefault(); }}
                >
                  <Icon className="nav-icon" {...NAV_ICON_PROPS} />
                  {!navCollapsed && <span className="nav-label">{item.label}</span>}
                </NavLink>
              );
            })}
            {!navCollapsed && (
              <>
                <div className="nav-logo">
                  <PulseMark size={26} trim />
                  <div className="nav-logo-text">
                    <span className="nav-logo-title">Pulse</span>
                    <span className="nav-logo-sub">BY AUMENTUM</span>
                  </div>
                </div>
                <div className="nav-copyright">{COPYRIGHT_NOTICE}</div>
              </>
            )}
          </nav>

          {/* MAIN CONTENT */}
          <main className="main-content">
            <Routes>
              <Route path="/"           element={<Dashboard />} />
              <Route path="/timeline"   element={<PortfolioTimeline />} />
              <Route path="/my-time"    element={<TimeEntry />} />
              <Route path="/resource-planning" element={<ResourcePlanning />} />
              <Route path="/time-reports" element={<TimeReports />} />
              {/* Deliberately not in NAV_ITEMS (Jeremy, 2026-10-07) -- reached from FCST vs Actuals
                  and Resource Planning > Forecasting, where these hours are already in view. */}
              <Route path="/rnd-time"   element={<RndTimeEntry />} />
              <Route path="/project-launch" element={<ProjectLaunch />} />
              {/* Project Setup became a tab on Project Launch (2026-09-07); SOW Intake moved again
                  (2026-09-08) into a placeholder's own Project Setup detail view rather than a
                  standalone tab (item 7), so there's no single page left for a bare /intake bookmark
                  to land on -- same reasoning as the RAAIDD redirect below, old links still land
                  somewhere sensible instead of 404ing. */}
              <Route path="/intake" element={<Navigate to="/project-launch" replace />} />
              <Route path="/setup" element={<Navigate to="/project-launch?view=setup" replace />} />
              {SCHEDULING_ENABLED && <Route path="/schedule" element={<ProjectSchedule />} />}
              <Route path="/registers" element={<ProjectRegisters />} />
              <Route path="/project-budgets" element={<ProjectBudgets />} />
              <Route path="/billing-rates" element={<BillingRates />} />
              <Route path="/milestone-tracker" element={<MilestoneTracker />} />
              <Route path="/revenue-tracker" element={<RevenueTracker />} />
              <Route path="/calendar" element={<ProjectCalendar />} />
              <Route path="/links" element={<ProjectLinks />} />
              {/* The RAAIDD register's old standalone route, kept as a redirect: it shipped to
                  production on 2026-09-02 and is linked from the help center and people's
                  bookmarks. */}
              <Route path="/risk-register" element={<Navigate to="/registers?tab=raaidd" replace />} />
              <Route path="/bpa"        element={<BPATracker />} />
              <Route path="/administration" element={<Administration />} />
              {/* The four pages Administration folded into tabs, kept as redirects -- same reasoning
                  as the RAAIDD redirect above, old links (bookmarks, the help center) still land
                  somewhere sensible instead of 404ing. */}
              <Route path="/resource-configuration" element={<Navigate to="/administration?tab=people" replace />} />
              <Route path="/project-configuration" element={<Navigate to="/administration?tab=projects" replace />} />
              <Route path="/ai-settings" element={<Navigate to="/administration?tab=ai-settings" replace />} />
              <Route path="/external-users" element={<Navigate to="/administration?tab=customer-access" replace />} />
              <Route path="/config"     element={<ConfigTracker />} />
              <Route path="/conversion" element={<DataConversion />} />
              <Route path="/testing"    element={<SolutionTesting />} />
              <Route path="/training"   element={<TrainingTracker />} />
              <Route path="/golive"     element={<GoLiveReadiness />} />
              <Route path="/tickets"    element={<TicketStatus />} />
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/login-history" element={<LoginHistory />} />
              <Route path="/suggestions" element={<SuggestionReviews />} />
            </Routes>
          </main>

        </div>

        {/* Inside the Router — the drawer picks its opening topic from the current route. */}
        <HelpDrawer
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          onAskPeter={() => setPeterOpenExclusive(true)}
          focusTopicId={pendingHelpTopicId}
          onFocusTopicConsumed={() => setPendingHelpTopicId(null)}
        />

        {/* Also inside the Router (needs the current route) and also outside <Routes> — mounted once
            for the app's lifetime so a conversation with Peter survives navigating between pages. */}
        <PeterWidget
          open={peterOpen}
          onOpenChange={setPeterOpenExclusive}
          hidden={helpOpen}
          onCiteTopic={openHelpAtTopic}
        />
      </div>
    </Router>
  );
}

export default App;